import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, SearchInput } from '@/components';
import { TableChip } from '../../components/PremiumDesign';
import { V2Table } from '@/common/components';
import { getWhatsAppTemplates, getGmailTemplates } from '../../api';
import { useDelayedSkeleton } from '@/common/hooks';

function categoryLabel(template) {
  const cat = (template?.category || '').toUpperCase();
  if (cat === 'MARKETING') return 'Promotion';
  if (cat === 'AUTHENTICATION') return 'Verification';
  if (cat === 'UTILITY') return 'Notification';
  return template?.category || 'General';
}

export function ReminderImportTemplateStep({
  companyId,
  selectedTemplateId,
  onSelect,
  enabled = true,
  channel = 'whatsapp',
}) {
  const [searchQuery, setSearchQuery] = useState('');

  const isEmail = channel === 'email';

  const {
    data: templatesRaw,
    isLoading: isTemplatesLoading,
    isFetching: isTemplatesFetching,
  } = useQuery({
    queryKey: ['wa-templates-import', companyId, searchQuery],
    queryFn: () =>
      getWhatsAppTemplates(companyId, {
        search: searchQuery || undefined,
      }),
    enabled: enabled && !isEmail && !!companyId,
  });

  const {
    data: emailTemplatesRaw,
    isLoading: isEmailTemplatesLoading,
    isFetching: isEmailTemplatesFetching,
  } = useQuery({
    queryKey: ['gmail-templates-import', companyId, searchQuery],
    queryFn: () =>
      getGmailTemplates(companyId, {
        search: searchQuery || undefined,
      }),
    enabled: enabled && isEmail && !!companyId,
  });

  const showTemplateSkeleton = useDelayedSkeleton(
    (isTemplatesFetching || isEmailTemplatesFetching) &&
      (templatesRaw !== undefined || emailTemplatesRaw !== undefined),
  );

  const isLoadingTemplates = isEmail
    ? isEmailTemplatesLoading || showTemplateSkeleton
    : isTemplatesLoading || showTemplateSkeleton;

  const approvedTemplates = useMemo(() => {
    if (isEmail) {
      const list = Array.isArray(emailTemplatesRaw)
        ? emailTemplatesRaw
        : emailTemplatesRaw?.results || emailTemplatesRaw?.data || [];
      return list.map((t) => ({
        id: t.id,
        name: t.name,
        template_name: t.name,
        category: t.subject || 'Notification',
        language: 'en_US',
        status: 'APPROVED',
        subject: t.subject,
        body: t.body,
      }));
    }
    const list = Array.isArray(templatesRaw)
      ? templatesRaw
      : templatesRaw?.results || templatesRaw?.data || [];
    return list.filter(
      (t) => (t.status || '').toUpperCase() === 'APPROVED' && t.id,
    );
  }, [isEmail, templatesRaw, emailTemplatesRaw]);

  const rawForCount = isEmail ? emailTemplatesRaw : templatesRaw;
  const reportedCount = Number(rawForCount?.count);
  const templatesArePartial =
    Number.isFinite(reportedCount) && reportedCount > approvedTemplates.length;

  const columns = useMemo(
    () => [
      {
        id: 'name',
        label: 'Name',
        defaultWidth: 180,
        minWidth: 120,
        cell: (template) => (
          <span className="truncate block" title={template.name}>
            {template.name}
          </span>
        ),
      },
      {
        id: 'type',
        label: 'Type',
        defaultWidth: 130,
        minWidth: 100,
        cell: (template) => categoryLabel(template),
      },
      {
        id: 'language',
        label: 'Language',
        defaultWidth: 110,
        minWidth: 90,
        cell: (template) => template.language || '-',
      },
      {
        id: 'status',
        label: 'Status',
        defaultWidth: 110,
        minWidth: 90,
        cell: () => <TableChip tone="success">Active</TableChip>,
      },
      {
        id: 'actions',
        label: 'Actions',
        defaultWidth: 110,
        minWidth: 100,
        align: 'center',
        stopPropagation: true,
        cell: (template) => {
          const isSelected = String(selectedTemplateId) === String(template.id);
          return (
            <div className="flex items-center justify-center">
              <Button
                variant={isSelected ? 'secondary' : 'primary'}
                className="h-8 px-3 text-xs"
                onClick={() => handleSelect(template)}
              >
                {isSelected ? 'Selected' : 'Select'}
              </Button>
            </div>
          );
        },
      },
    ],
    // handlers are stable for the lifetime of this step
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedTemplateId],
  );

  const handleSelect = (template) => {
    onSelect(template);
  };

  return (
    <div className="flex flex-col gap-4 flex-1 min-h-0 h-full">
      {/* <p className="text-sm text-text-secondary shrink-0">
          Pick an APPROVED WhatsApp template. Preview before selecting — same
          message goes to every contact in your import file.
        </p> */}

      <SearchInput
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        placeholder="Search templates"
        className="w-full sm:max-w-md shrink-0"
      />

      <div className="w-full min-w-0 flex-1 min-h-0 flex flex-col">
        {templatesArePartial ? (
          <p className="text-xs text-text-muted px-1 pb-2 shrink-0">
            Showing {approvedTemplates.length} loaded of {reportedCount}.
          </p>
        ) : null}
        <V2Table
          tight
          storageKey={
            isEmail
              ? 'reminder_import_email_templates_column_widths'
              : 'reminder_import_whatsapp_templates_column_widths'
          }
          columns={columns}
          data={approvedTemplates}
          rowKey="id"
          loading={isLoadingTemplates || (!isEmail && !templatesRaw)}
          isRowSelected={(template) =>
            String(selectedTemplateId) === String(template.id)
          }
          empty="No templates found."
        />
      </div>
    </div>
  );
}
